<template>
  <Application>
    <b-breadcrumb :items="items"></b-breadcrumb>

    <b-row>
      <b-col>
        <b-overlay :show="isLoadingTeam" rounded="sm">
          <b-card no-body>
            <b-tabs pills card>
              <b-tab title="Dados da equipe" active>
                <b-row class="mb-2">
                  <b-col>
                    <strong>Informações da equipe</strong>
                  </b-col>
                </b-row>

                <b-row class="mb-3">
                  <b-col lg="6">
                    <label>
                      Nome <span class="text-danger">*</span>
                    </label>
                    <b-input v-model.trim="team.title"/>
                  </b-col>
                  <b-col lg="6">
                    <label>
                      Responsável <span class="text-danger">*</span>
                    </label>
                    <b-form-select
                      v-model.trim="team.leaderId"
                      :options="managers"
                    />
                  </b-col>
                </b-row>

                <b-row class="mb-3">
                  <b-col>
                    <label>
                      Descrição
                    </label>
                    <b-input v-model.trim="team.description"/>
                  </b-col>

                  <b-col>
                    <label>
                      URL <span class="text-danger">*</span>
                    </label>
                    <b-input v-model.trim="team.slug"/>
                  </b-col>
                </b-row>

                <b-row class="mb-2">
                  <b-col>
                    <strong>Membros da equipe</strong>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col>
                    <b-table
                      small
                      striped
                      bordered
                      responsive="lg"
                      :fields="fields"
                      :busy="isLoadingUsers"
                      :items="users"
                      :per-page="perPage"
                      :current-page="currentPage"
                    >
                      <template v-slot:cell(actions)="data">
                        <b-form-checkbox
                          v-model="team.members"
                          name="team-members"
                          :value="data.item.uid"
                        />
                      </template>

                      <template v-slot:cell(type)="data">
                        <div class="text-center">
                          <span :class="(data.item.type === 1)
                            ? 'font-weight-bold text-primary'
                            : ''"
                          >
                            {{getUserDescription(data.item.type)}}
                          </span>
                        </div>
                      </template>

                      <template v-slot:table-busy>
                        <div class="d-flex align-items-center justify-content-center p-4">
                          <b-spinner></b-spinner>
                          <small class="ml-2 font-weight-bold">Carregando usuários</small>
                        </div>
                      </template>
                    </b-table>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col>
                    <b-pagination
                      class="mb-0"
                      v-model="currentPage"
                      :total-rows="rows"
                      :per-page="perPage"
                    />
                  </b-col>
                </b-row>

                <b-row>
                  <b-col>
                    <hr>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col class="text-right">
                    <b-button
                      variant="primary"
                      size="sm"
                      :disabled="isInvalid"
                      @click="submit()"
                    >
                      Salvar
                    </b-button>
                  </b-col>
                </b-row>
              </b-tab>
              <b-tab title="Tarefas" v-if="mode !== 'CREATE'" lazy>
                <Kanban
                  :isLoadingTasks="isLoadingTasks"
                  :boards="project.boards || []"
                  :tasks="project.tasks || []"
                  :info="project.info || {}"
                  :teamSlug="this.$route.params.slug"
                  @change-broker="fetchTasksByBroker"
                  @reload-tasks="reloadTasks"
                />
              </b-tab>
            </b-tabs>
          </b-card>
        </b-overlay>
      </b-col>
    </b-row>
  </Application>
</template>

<script>
import { mapState } from 'vuex';
import Application from '@/components/Application/Application.vue';
import Kanban from '@/components/Projects/Kanban.vue';
import { UsersService, TeamsService, ProjectsService } from '@/services';
import UserTypesEnum from '@/enums/userTypes';
import CRMButton from '@/components/CRMButton/CRMButton.vue';

export default {
  components: {
    Application,
    Kanban,
    CRMButton,
  },

  data() {
    return {
      isLoadingTeam: false,
      isLoadingTasks: false,
      isLoadingUsers: false,
      mode: '',
      project: {},
      items: [
        { text: 'Dashboard', to: '/' },
        { text: 'Equipes', to: '/teams' },
        { text: '', active: true },
      ],
      team: {
        title: '',
        description: '',
        leaderId: '',
        slug: '',
        members: [],
      },
      currentPage: 1,
      perPage: 10,
      fields: [
        { key: 'actions', label: '', class: 'text-center' },
        { key: 'name', label: 'Nome completo' },
        { key: 'type', label: 'Tipo', class: 'text-center' },
      ],
    };
  },

  async mounted() {
    if (this.$route.params.slug === 'new') {
      this.mode = 'CREATE';

      this.fetchUsers();
    } else {
      this.mode = 'EDIT';
      this.fetchTeamInfo();
    }
  },

  methods: {
    async fetchTeamInfo() {
      this.isLoadingTeam = true;

      /* TODO: Refactor to vuex */
      const team = await TeamsService
        .fetchTeamBySlug({
          companyId: this.user.company_id,
          slug: this.$route.params.slug,
        });

      const members = await TeamsService.fetchTeamMembers(team.uid);

      this.team = {
        uid: team.uid,
        title: team.title,
        description: team.description,
        leaderId: team.leader_id,
        slug: team.slug,
        members: members.map(({ user_id: uid }) => uid),
      };

      this.project = await this.fetchTasks();

      this.fetchUsers();

      this.isLoadingTeam = false;
    },

    async fetchTasks() {
      this.isLoadingTasks = true;

      const info = await ProjectsService
        .fetchDefaultProjectInfo({ companyId: this.user.company_id });

      const boards = await ProjectsService.fetchProjectBoards(info.uid);
      const tasks = await ProjectsService.fetchProjectTasks({ projectId: info.uid });
      const teamTasks = tasks.filter((task) => this.team.members.indexOf(task.assignee_id) > -1);

      this.isLoadingTasks = false;

      return {
        info, boards, tasks: teamTasks,
      };
    },

    fetchUsers() {
      this.isLoadingUsers = true;

      UsersService
        .fetchUserProfilesByCompany(this.user.company_id)
        .then((users) => {
          this.$store.commit('settings/SET_USERS', users);

          this.isLoadingUsers = false;
        })
        .catch((err) => {
          console.log(err);
        });
    },

    getUserDescription(type) {
      return UserTypesEnum.getValue(type);
    },

    async createTeam() {
      try {
        this.isLoadingTeam = true;

        const uid = await TeamsService.createTeam({
          companyId: this.user.company_id,
          ...this.team,
        });

        this.team.uid = uid;

        await TeamsService.updateTeamInfo(
          this.team.uid,
          {
            ...this.team,
            members: this.team.members.filter((member) => member !== this.team.leaderId),
          },
        );

        this.isLoadingTeam = false;

        this.$store.commit('global/SET_NOTIFICATION', {
          variant: 'success',
          content: 'Equipe criada com sucesso',
          title: 'Sucesso!',
        }, { root: true });
      } catch (err) {
        console.log(err);

        this.$store.commit('global/SET_NOTIFICATION', {
          variant: 'danger',
          content: 'Não foi possível criar a equipe',
          title: 'Ops! Encontramos um problema',
        }, { root: true });
      }
    },

    async updateTeam() {
      try {
        this.isLoadingTeam = true;

        await TeamsService.updateTeamInfo(
          this.team.uid,
          {
            ...this.team,
            members: this.team.members.filter((member) => member !== this.team.leaderId),
          },
        );

        this.$store.commit('global/SET_NOTIFICATION', {
          variant: 'success',
          content: 'Dados da equipe atualizados com sucesso',
          title: 'Sucesso!',
        }, { root: true });
      } catch (err) {
        console.log(err);

        this.$store.commit('global/SET_NOTIFICATION', {
          variant: 'danger',
          content: 'Não foi possível atualizar as informações da equipe',
          title: 'Ops! Encontramos um problema',
        }, { root: true });
      }

      this.isLoadingTeam = false;
    },

    async submit() {
      if (this.mode !== 'CREATE') {
        await this.updateTeam();
      } else {
        await this.createTeam();

        this.project = await this.fetchTasks();
        this.mode = 'EDIT';
      }
    },

    async fetchTasksByBroker(brokerId) {
      this.isLoadingTasks = true;

      this.project.tasks = await ProjectsService.fetchProjectTasks({
        projectId: this.project.info.uid,
        userId: this.user.uid,
        brokerId,
      });

      this.isLoadingTasks = false;
    },

    async reloadTasks() {
      this.isLoadingTasks = true;

      const tasks = await ProjectsService.fetchProjectTasks({ projectId: this.project.info.uid });
      const teamTasks = tasks.filter((task) => this.team.members.indexOf(task.assignee_id) > -1);

      this.project.tasks = teamTasks;

      this.isLoadingTasks = false;
    },
  },

  computed: {
    ...mapState(['teams', 'settings']),

    rows() {
      return (this.settings.users)
        ? this.settings.users.length
        : 0;
    },

    users() {
      return (this.settings.users || []).filter((user) => user.uid !== this.team.leaderId);
    },

    managers() {
      return (this.settings.users || [])
        .filter((user) => user.type <= 2)
        .map(({ name, uid }) => ({ text: name, value: uid }));
    },

    isInvalid() {
      return !this.team.title
        || !this.team.leaderId
        || !this.team.slug;
    },
  },
};
</script>
